import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X, ArrowUpRight, ShieldCheck } from "@phosphor-icons/react";
import { SITE } from "@/lib/site";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Products" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#fe4c1c] bg-[#fe4c1c]/10"
                    : "text-slate-700 hover:text-[#fe4c1c] hover:bg-slate-50"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={SITE.verifyProUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-verifypro-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#fe4c1c] hover:bg-[#e63d0e] transition-colors shadow-sm shadow-[#fe4c1c]/30"
          >
            <ShieldCheck size={16} weight="bold" /> Launch VerifyPro
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-[#1f2a44]"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="px-5 py-6 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${
                    isActive
                      ? "text-[#fe4c1c] bg-[#fe4c1c]/10"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={SITE.verifyProUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-verifypro-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#fe4c1c]"
            >
              <ShieldCheck size={16} weight="bold" /> Launch VerifyPro
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
